import {organizationApiClient} from 'organization/organizationApiClient'
import React, {useEffect, useState} from 'react'
import {useRouteOrganizationEventId} from 'utils/lib/url'

export type Invite = {
  id: number
  email: string
  reservation: boolean
  created_at: Date
}

export interface EventInvitesContextProps {
  invites: Invite[]
  loading: boolean
  page: number
  totalPages: number
  total: number
  from: number
  to: number
  changePage: (e: any, page: number) => void
  search: (search: string) => void
  remove: (invite: Invite) => void
  refresh: () => void
}

export const EventInvitesContext = React.createContext<
  EventInvitesContextProps | undefined
>(undefined)

export default function EventInvitesProvider(props: {
  children: React.ReactElement
}) {
  const apiClient = organizationApiClient()
  const [invites, setInvites] = useState<Invite[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [total, setTotal] = useState<number>(0)
  const [from, setFrom] = useState<number>(0)
  const [to, setTo] = useState<number>(0)
  const [lastSearch, setLastSearch] = useState<string>('')
  const [page, setPage] = useState<number>(1)
  const eventId = useRouteOrganizationEventId()

  useEffect(() => {
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const get = async (getPage?: number, search?: string) => {
    setLoading(true)
    const selectedPage = getPage ?? page
    setLastSearch(search ?? '')
    await apiClient
      .get(
        `api/event/${eventId}/invites?page=${selectedPage}&search=${search ?? ''}`,
      )
      .then((response) => {
        setInvites(response.data)
        setTotalPages(response.last_page)
        setPage(response.current_page)
        setTotal(response.total)
        setFrom(response.from)
        setTo(response.to)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const remove = async (invite: Invite) => {
    await apiClient
      .remove(`api/event/${eventId}/invites/${invite.id}`)
      .then(() => refresh())
      .finally(() => {
        setLoading(false)
      })
  }

  const changePage = (e: any, toPage: number) => {
    setPage(toPage)
    get(toPage)
  }
  const search = async (search: string) => {
    return get(1, search)
  }

  const refresh = () => {
    get(page, lastSearch)
  }

  return (
    <EventInvitesContext.Provider
      value={{
        invites,
        loading,
        page,
        changePage,
        totalPages,
        total,
        from,
        to,
        search,
        remove,
        refresh,
      }}
    >
      {props.children}
    </EventInvitesContext.Provider>
  )
}

export function useEventInvites() {
  const context = React.useContext(EventInvitesContext)
  if (context === undefined) {
    throw new Error('useEventInvites must be used within EventInvitesProvider')
  }
  return context
}
